import React, { useState, useMemo } from 'react';
import { Pressable, StyleSheet, Platform, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import Icon from '@/components/Icon';

const createStyles = (colors) => StyleSheet.create({
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    backgroundColor: 'transparent',
    borderRadius: 17,
    height: 34,
    minWidth: 34,
    paddingHorizontal: 8,
    ...Platform.select({
      web: {
        transition: 'all 0.2s ease',
        cursor: 'pointer',
      }
    })
  },
  buttonHovered: {
    backgroundColor: colors.solid_color_tertiary,
  },
  inner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  buttonDot: {
    position: 'absolute',
    top: 3,
    right: 3,
    width: 10,
    height: 10,
    borderRadius: 10,
    backgroundColor: 'rgb(255, 40, 40)',
    borderWidth: 2,
    borderColor: colors.solid_color_primary,
  },
  plusIcon: {
    fontSize: 20,
    fontWeight: '400',
    color: colors.text_color_primary,
    lineHeight: 22,
  },
});

const ComposerToolsButton = React.forwardRef(function ComposerToolsButton({
  onPress,
  isMissingRequiredParameters,
  asDropdownTrigger = false,
  ...triggerProps
}, forwardedRef) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const [isHovered, setIsHovered] = useState(false);
  const TriggerComponent = Platform.OS === 'web' && asDropdownTrigger ? View : Pressable;

  return (
    <TriggerComponent
      {...triggerProps}
      ref={forwardedRef}
      hitSlop={{ top: 5, bottom: 5, left: 5, right: 5 }}
      style={StyleSheet.flatten([styles.button, isHovered && styles.buttonHovered])}
      onPress={onPress}
      onMouseEnter={Platform.OS === 'web' ? () => setIsHovered(true) : undefined}
      onMouseLeave={Platform.OS === 'web' ? () => setIsHovered(false) : undefined}
      onHoverIn={Platform.OS !== 'web' ? () => setIsHovered(true) : undefined}
      onHoverOut={Platform.OS !== 'web' ? () => setIsHovered(false) : undefined}
      accessibilityRole="button"
      accessibilityLabel={t('chatInput.openComposerTools') || 'Add files, tools and parameters'}
      accessibilityHint={isMissingRequiredParameters
        ? (t('chatInput.missingRequiredParametersHint') || 'Required parameters are missing')
        : undefined}
    >
      <View style={styles.inner}>
        {/* Plus icon - similar to Claude and ChatGPT */}
        <Icon name="plus" size={18} color={colors.text_color_primary} />
      </View>
      {isMissingRequiredParameters ? <View style={styles.buttonDot} /> : null}
    </TriggerComponent>
  );
});

ComposerToolsButton.displayName = 'ComposerToolsButton';

export default ComposerToolsButton;
